/** API_SPEC.md §4 — resumes, uploads, health, rebuild. */
import { API_BASE, ApiError, api } from './client';
import type {
  DraftScore,
  DraftScoreRequest,
  ImportAccepted,
  PdfExportJob,
  RebuildRequest,
  ResumeCreateRequest,
  ResumeCreated,
  ResumeDetail,
  ResumeHealth,
  ResumeKind,
  ResumeListResponse,
  ResumePatch,
  ResumePatchResult,
  ResumeSummary,
  TailorRun,
  TemplateId,
  Upload,
} from './types';

export const resumesApi = {
  list: (kind: ResumeKind | 'all' = 'user') => api<ResumeListResponse>('/v1/resumes', { query: { kind } }),
  create: (body: ResumeCreateRequest) => api<ResumeCreated>('/v1/resumes', { method: 'POST', body }),
  /** Two steps by design: 202 here, then poll `upload()` until `parsed`, then `create({fromUploadId})`. */
  import: (file: File, templateId?: TemplateId) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    if (templateId) fd.append('templateId', templateId);
    return api<ImportAccepted>('/v1/resumes/import', { method: 'POST', formData: fd });
  },
  upload: (uploadId: string) => api<Upload>(`/v1/uploads/${encodeURIComponent(uploadId)}`),
  get: (id: string) => api<ResumeDetail>(`/v1/resumes/${encodeURIComponent(id)}`),
  /** `docVersion` must match or the server answers 409 `version_conflict {currentVersion}`. */
  patch: (id: string, body: ResumePatch) => api<ResumePatchResult>(`/v1/resumes/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  duplicate: (id: string, name?: string) => api<ResumeCreated>(`/v1/resumes/${encodeURIComponent(id)}/duplicate`, { method: 'POST', body: name ? { name } : {} }),
  remove: (id: string) => api<void>(`/v1/resumes/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** `targetRole` is a New Health Check's role, stored on that report only (never on the account); blank = the account's. */
  health: (id: string, refresh = false, targetRole?: string) => api<ResumeHealth>(`/v1/resumes/${encodeURIComponent(id)}/health`, { query: { refresh: refresh ? 'true' : undefined, targetRole: targetRole?.trim() || undefined } }),
  /** Ux-bug-pass-7 ticket 07: the health score of an unsaved document. Stores nothing, spends no analysis, its own rate limit. */
  draftScore: (id: string, body: DraftScoreRequest, signal?: AbortSignal) => api<DraftScore>(`/v1/resumes/${encodeURIComponent(id)}/draft-score`, { method: 'POST', body, signal }),
  rebuild: (id: string, body: RebuildRequest, idempotencyKey: string) => api<TailorRun>(`/v1/resumes/${encodeURIComponent(id)}/rebuild`, { method: 'POST', body, idempotencyKey }),
  exportEvent: (id: string, templateId: TemplateId) => api<void>(`/v1/resumes/${encodeURIComponent(id)}/export-events`, { method: 'POST', body: { format: 'pdf', templateId } }),
  /** Skill Bank tailor (13): put a Max Match cut back. 200 is the whole résumé again; 404 is an unknown or already-restored cut. */
  restoreCut: (id: string, cutId: string) => api<ResumeDetail>(`/v1/resumes/${encodeURIComponent(id)}/cuts/${encodeURIComponent(cutId)}/restore`, { method: 'POST' }),
  restore: (id: string, docVersion: number) => api<ResumeDetail>(`/v1/resumes/${encodeURIComponent(id)}/restore`, { method: 'POST', body: { docVersion } }),
  /** Ticket 37: server-side PDF render (`app/services/exports`), replacing
   *  the old client-side `html2canvas`/`jsPDF` screenshot path. 202 —
   *  poll `pdfExportStatus` until `succeeded`, then `pdfExportDownloadUrl`. */
  startPdfExport: (id: string) => api<PdfExportJob>(`/v1/resumes/${encodeURIComponent(id)}/export/pdf`, { method: 'POST' }),
  pdfExportStatus: (id: string, exportId: string) => api<PdfExportJob>(`/v1/resumes/${encodeURIComponent(id)}/export/pdf/${encodeURIComponent(exportId)}`),
  /** Not fetched through `api()` — this is navigated to directly (a same-
   *  origin, cookie-authenticated `<a download>`, `ResumeEditor.tsx`'s
   *  `triggerDownload`) so the browser streams the response straight to
   *  disk instead of buffering it through `fetch`/a blob URL. */
  pdfExportDownloadUrl: (id: string, exportId: string) => `${API_BASE}/v1/resumes/${encodeURIComponent(id)}/export/pdf/${encodeURIComponent(exportId)}/download`,
};

/** A same-origin, cookie-authenticated file download triggered without navigating the SPA away: the `download`
 *  attribute both forces "save" over "open" and names the file, overriding the server's own filename. */
function triggerDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** The saved file's name: the name on the résumé and the minute it was saved, `Jordan_Rivera_Resume_202610101230.pdf`. */
export function resumePdfFilename(name: string | undefined, now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${(name || 'Resume').trim().replace(/\s+/g, '_') || 'Resume'}_Resume_${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}${p(now.getHours())}${p(now.getMinutes())}.pdf`;
}

/**
 * The server-side PDF export every screen shares (backend ticket 37): `POST …/export/pdf`, poll at the cadence the
 * other job polls use (no faster than the backend's 1/s/resource limit) until `succeeded`, then hand the file to the
 * browser. Throws with a sentence for a failed or stalled render. `report` hears each step ("Rendering…", "Saving…")
 * and may return a promise to hold the next one until the step has been painted. The callers keep what is theirs:
 * the editor saves its edits first and the template gate; Action's sheets read the template before they start.
 */
export async function downloadResumePdf(resumeId: string, filename: string, report: (step: string) => void | Promise<void> = () => undefined): Promise<void> {
  const job = await resumesApi.startPdfExport(resumeId);
  await report('Rendering…');
  const deadline = Date.now() + 90_000; // generous over the backend's own 2×30s render budget
  for (;;) {
    const status = await resumesApi.pdfExportStatus(resumeId, job.exportId);
    if (status.status === 'succeeded') break;
    if (status.status === 'failed') throw new Error('Rendering failed. Try again.');
    if (Date.now() > deadline) throw new Error('Rendering is taking too long. Try again.');
    await new Promise((r) => setTimeout(r, 1200));
  }
  await report('Saving…');
  triggerDownload(resumesApi.pdfExportDownloadUrl(resumeId, job.exportId), filename);
}

/** How long an import may stay `parsing` before the caller gives up (40 polls of 1.5 s, as Rebuild's own wait). */
const IMPORT_POLLS = 40;
const IMPORT_POLL_MS = 1500;

/**
 * The import path every screen shares, as one call: `POST /v1/resumes/import`, poll
 * `GET /v1/uploads/{id}` until `parsed`, then `POST /v1/resumes {fromUploadId}`. The new
 * résumé is a library résumé, not made master (the API makes the first one master).
 * Throws the API's own error for a refused import or a résumé cap (`toUiError` maps it),
 * and an `unreadable_file` `ApiError`-shaped failure when the parse failed.
 */
export async function importResume(file: File, templateId?: TemplateId): Promise<ResumeCreated> {
  const acc = await resumesApi.import(file, templateId);
  for (let i = 0; i < IMPORT_POLLS; i++) {
    const up = await resumesApi.upload(acc.uploadId);
    if (up.status === 'parsed') return resumesApi.create({ fromUploadId: acc.uploadId, ...(templateId ? { templateId } : {}) });
    if (up.status === 'failed') throw new ApiError({ status: 422, code: 'unreadable_file', message: "We couldn't read that file." });
    await new Promise((r) => setTimeout(r, IMPORT_POLL_MS));
  }
  throw new ApiError({ status: 0, code: 'network', message: 'Reading that file is taking longer than expected. Try again in a moment.' });
}

export function masterOf(items: ResumeSummary[] | undefined): ResumeSummary | null {
  if (!items?.length) return null;
  // Pass a list of `all` (ticket 16): once a Rebuild has run, the master is a
  // `rebuild_output`, which `kind=user` does not return — so a `user` list
  // would fall through to some other document and call it the master.
  // The fallback skips `tailored` and `rebuild_output` for the same reason:
  // "whatever sorted first" is a job-specific copy as often as not.
  return items.find((r) => r.isMaster) ?? items.find((r) => r.kind === 'user') ?? null;
}
